import { useLogout, useUser } from "@fideus/auth";
import { LoadingPage } from "@fideus-ui/core";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient, useTRPC } from "../_app/api";
import { useTranslation } from "../_app/i18n";
import { AccessRequested } from "./AccessRequested";
import { RequestAccess } from "./RequestAccess";
import { translations } from "./translations";

interface AccessGuardProps {
  children: ReactNode;
}

export function AccessGuard({ children }: AccessGuardProps) {
  const t = useTranslation(translations);
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { firstName, email, id, lastName } = useUser();
  const logout = useLogout();
  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  if (!email) {
    throw new Error("Logged in via method that doesn't reveal email");
  }
  const { data, error } = useQuery(trpc.me.queryOptions());
  const { mutate: requestAccess, error: mutationError } = useMutation(
    trpc.requestAccess.mutationOptions({
      onMutate: async () => {
        await queryClient.cancelQueries(trpc.me.queryOptions());
        const previousUser = queryClient.getQueryData(
          trpc.me.queryOptions().queryKey
        );
        queryClient.setQueryData(trpc.me.queryOptions().queryKey, {
          id: id,
          email: email,
          fullName,
          status: "accessRequested",
          createdAt: new Date(),
          updatedAt: new Date(),
        });
        return { previousUser };
      },
      onError: (_err, _newTodo, context) => {
        queryClient.setQueryData(
          trpc.me.queryOptions().queryKey,
          context?.previousUser
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries(trpc.me.queryOptions());
      },
    })
  );

  const isSsoRequired = mutationError?.message === "SSO_REQUIRED";

  if (!data && !error) {
    return <LoadingPage />;
  }

  if (error) {
    return <RequestAccess onLogout={logout} />;
  }

  if (data.status === "noAccess") {
    return (
      <RequestAccess
        onRequestAccess={() => requestAccess()}
        onLogout={logout}
        error={isSsoRequired ? t("ssoRequiredError") : undefined}
      />
    );
  }

  if (data.status === "accessRequested") {
    return <AccessRequested onLogout={logout} />;
  }

  if (data.status === "accessGranted") {
    return <>{children}</>;
  }

  return null;
}
